import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useToast } from '../hooks/useToast'
import ModalConfirmacion from '../components/ModalConfirmacion'

// Helper para formatear como pesos chilenos sin decimales
const formatoPesosChilenos = (valor) => {
  if (!valor && valor !== 0) return ''
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(valor)
}

export default function ClienteDetalle() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [cliente, setCliente] = useState(null)
  const [servicios, setServicios] = useState([])
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [showServicioForm, setShowServicioForm] = useState(false)
  const [editingServicio, setEditingServicio] = useState(null)

  // Estados para modales de confirmación
  const [modalCliente, setModalCliente] = useState(false)
  const [modalServicio, setModalServicio] = useState({ isOpen: false, servicioId: null })

  // Form cliente
  const [nombre, setNombre] = useState('')
  const [email, setEmail] = useState('')
  const [telefono, setTelefono] = useState('')
  const [empresa, setEmpresa] = useState('')
  const [notas, setNotas] = useState('')

  // Form servicio
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [precio, setPrecio] = useState('')
  const [estado, setEstado] = useState('pendiente')
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')

  const { addToast } = useToast()

  // Cargar datos del cliente
  const fetchCliente = useCallback(async () => {
    if (!id) return
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      addToast('Error al cargar el cliente', 'error')
      navigate('/clientes')
      return
    }
    setCliente(data)
    setNombre(data.nombre)
    setEmail(data.email || '')
    setTelefono(data.telefono || '')
    setEmpresa(data.empresa || '')
    setNotas(data.notas || '')
    setLoading(false)
  }, [id, navigate, addToast])

  // Cargar servicios del cliente
  const fetchServicios = useCallback(async () => {
    if (!id) return
    const { data, error } = await supabase
      .from('servicios')
      .select('*')
      .eq('cliente_id', id)
      .order('created_at', { ascending: false })

    if (error) {
      addToast('Error al cargar los servicios', 'error')
    } else {
      setServicios(data || [])
    }
  }, [id, addToast])

  // Efecto inicial
  useEffect(() => {
    if (id) {
      fetchCliente()
      fetchServicios()
    }
  }, [id, fetchCliente, fetchServicios])

  // Actualizar cliente
  const handleUpdateCliente = async (e) => {
    e.preventDefault()
    const { error } = await supabase
      .from('clientes')
      .update({ nombre, email, telefono, empresa, notas })
      .eq('id', id)

    if (error) {
      addToast('Error al actualizar el cliente', 'error')
    } else {
      addToast('Cliente actualizado correctamente', 'success')
      setEditMode(false)
      fetchCliente()
    }
  }

  // Eliminar cliente (confirmado en modal)
  const handleDeleteCliente = async () => {
    const { error } = await supabase
      .from('clientes')
      .delete()
      .eq('id', id)

    if (error) {
      addToast('Error al eliminar el cliente', 'error')
    } else {
      addToast('Cliente eliminado correctamente', 'success')
      navigate('/clientes')
    }
  }

  // Reiniciar formulario de servicio
  const resetServicioForm = () => {
    setTitulo('')
    setDescripcion('')
    setPrecio('')
    setEstado('pendiente')
    setFechaInicio('')
    setFechaFin('')
    setEditingServicio(null)
  }

  // Guardar o actualizar servicio
  const handleServicioSubmit = async (e) => {
    e.preventDefault()
    const user = (await supabase.auth.getUser()).data.user
    if (!user) return

    const servicioData = {
      titulo,
      descripcion,
      precio: parseFloat(precio) || 0,
      estado,
      fecha_inicio: fechaInicio || null,
      fecha_fin: fechaFin || null,
      cliente_id: id,
      user_id: user.id
    }

    if (editingServicio) {
      // Actualizar
      const { error } = await supabase
        .from('servicios')
        .update(servicioData)
        .eq('id', editingServicio.id)

      if (error) {
        addToast('Error al actualizar el servicio', 'error')
      } else {
        addToast('Servicio actualizado correctamente', 'success')
        setShowServicioForm(false)
        resetServicioForm()
        fetchServicios()
      }
    } else {
      // Crear nuevo
      const { error } = await supabase
        .from('servicios')
        .insert([servicioData])

      if (error) {
        addToast('Error al crear el servicio', 'error')
      } else {
        addToast('Servicio creado correctamente', 'success')
        setShowServicioForm(false)
        resetServicioForm()
        fetchServicios()
      }
    }
  }

  // Cargar datos para editar servicio
  const handleEditServicio = (servicio) => {
    setEditingServicio(servicio)
    setTitulo(servicio.titulo)
    setDescripcion(servicio.descripcion || '')
    setPrecio(servicio.precio ? String(servicio.precio) : '')
    setEstado(servicio.estado)
    setFechaInicio(servicio.fecha_inicio || '')
    setFechaFin(servicio.fecha_fin || '')
    setShowServicioForm(true)
  }

  // Eliminar servicio (llamado desde modal)
  const handleDeleteServicio = async (servicioId) => {
    const { error } = await supabase
      .from('servicios')
      .delete()
      .eq('id', servicioId)

    if (error) {
      addToast('Error al eliminar el servicio', 'error')
    } else {
      addToast('Servicio eliminado correctamente', 'success')
      fetchServicios()
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando cliente...</p>
        </div>
      </div>
    )
  }

  if (!cliente) {
    return (
      <div className="text-center mt-10">
        <p className="text-gray-500">Cliente no encontrado</p>
        <Link to="/clientes" className="text-blue-600 hover:underline mt-2 inline-block">
          Volver a clientes
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modales */}
      <ModalConfirmacion
        isOpen={modalCliente}
        onClose={() => setModalCliente(false)}
        onConfirm={handleDeleteCliente}
        title="Eliminar cliente"
        message="¿Estás seguro de eliminar este cliente? También se eliminarán todos sus servicios asociados. Esta acción no se puede deshacer."
      />
      <ModalConfirmacion
        isOpen={modalServicio.isOpen}
        onClose={() => setModalServicio({ isOpen: false, servicioId: null })}
        onConfirm={() => handleDeleteServicio(modalServicio.servicioId)}
        title="Eliminar servicio"
        message="¿Estás seguro de eliminar este servicio? Esta acción no se puede deshacer."
      />

      {/* Navegación */}
      <div className="mb-6">
        <Link to="/clientes" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver a clientes
        </Link>
      </div>

      {/* Tarjeta del cliente */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <h1 className="text-2xl font-semibold text-gray-800">{cliente.nombre}</h1>
          <div className="flex space-x-3">
            <button
              onClick={() => setEditMode(!editMode)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              {editMode ? 'Cancelar' : 'Editar'}
            </button>
            <button
              onClick={() => setModalCliente(true)}
              className="text-sm text-red-500 hover:text-red-600 font-medium transition-colors"
            >
              Eliminar
            </button>
          </div>
        </div>

        {editMode ? (
          <form onSubmit={handleUpdateCliente} className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Empresa</label>
                <input
                  type="text"
                  value={empresa}
                  onChange={(e) => setEmpresa(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Notas</label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                Guardar cambios
              </button>
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">📧</span>
              <span className="text-gray-600">{cliente.email || '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">📞</span>
              <span className="text-gray-600">{cliente.telefono || '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">🏢</span>
              <span className="text-gray-600">{cliente.empresa || '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">📝</span>
              <span className="text-gray-600">{cliente.notas || '—'}</span>
            </div>
          </div>
        )}
      </div>

      {/* Sección de servicios */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Servicios contratados</h2>
          <button
            onClick={() => {
              resetServicioForm()
              setShowServicioForm(!showServicioForm)
            }}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {showServicioForm ? 'Cancelar' : 'Añadir servicio'}
          </button>
        </div>

        {/* Formulario de servicio */}
        {showServicioForm && (
          <form onSubmit={handleServicioSubmit} className="mb-8 p-5 bg-gray-50 rounded-xl border border-gray-100">
            <h3 className="font-medium text-gray-700 mb-4">
              {editingServicio ? 'Editar servicio' : 'Nuevo servicio'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Título *</label>
                <input
                  type="text"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Descripción</label>
                <textarea
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Precio ($)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={precio}
                  onChange={(e) => setPrecio(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                  placeholder="0"
                />
                <p className="text-xs text-gray-400 mt-1">Ej: 495000 → $495.000</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Estado</label>
                <select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en curso">En curso</option>
                  <option value="finalizado">Finalizado</option>
                  <option value="facturado">Facturado</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Fecha inicio</label>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Fecha fin</label>
                <input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                />
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
              >
                {editingServicio ? 'Actualizar' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetServicioForm()
                  setShowServicioForm(false)
                }}
                className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        {/* Lista de servicios */}
        {servicios.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No hay servicios para este cliente.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {servicios.map((s) => (
              <div
                key={s.id}
                className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 mb-3 md:mb-0">
                  <h4 className="font-medium text-gray-800">{s.titulo}</h4>
                  {s.descripcion && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{s.descripcion}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 mt-2">
                    {s.fecha_inicio && (
                      <span>Inicio: {new Date(s.fecha_inicio).toLocaleDateString('es-CL')}</span>
                    )}
                    {s.fecha_fin && (
                      <span>Fin: {new Date(s.fecha_fin).toLocaleDateString('es-CL')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-3 md:flex-shrink-0">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      s.estado === 'finalizado'
                        ? 'bg-green-100 text-green-700'
                        : s.estado === 'en curso'
                        ? 'bg-yellow-100 text-yellow-700'
                        : s.estado === 'facturado'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {s.estado}
                  </span>
                  <span className="font-medium text-gray-700">
                    {s.precio ? formatoPesosChilenos(s.precio) : '—'}
                  </span>
                  <button
                    onClick={() => handleEditServicio(s)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => setModalServicio({ isOpen: true, servicioId: s.id })}
                    className="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}